/**
 * API通信のためのユーティリティ関数
 */
import appConfig, { fetchConfig } from '../config';

/**
 * ===== 一時トークン認証フロー =====
 *
 * システムの安全性を確保するため、以下の流れで一時トークン認証を実装しています：
 *
 * 1. クライアント側：
 *    a. /api/temp_token.jsonから生のトークンを取得
 *    b. トークンをconvertString関数で変換（暗号化）
 *    c. 変換後のトークンをリクエストヘッダ(X-Temp-Token)に付与してAPIを呼び出し
 *
 * 2. サーバー側：
 *    a. 受信したトークンをconvert_string関数で元に戻す（復号）
 *    b. 復元されたトークンと、その時点で生成したオリジナルトークンを比較
 *    c. 一致すれば認証成功、不一致なら401エラー
 *
 * トークンの変換処理：
 * - インデックス1と現在時刻の「分」に対応するインデックスの文字を入れ替え
 * - 文字列の前半（20文字）は2文字ずつ入れ替え
 * - 文字列の後半（21文字目以降）は文字の順序を逆転
 *
 * ※このフローにより、単純なトークンの使い回しや推測が困難になり、
 *   セッションハイジャックなどのセキュリティリスクを軽減しています。
 */

/**
 * 一時トークン管理ユーティリティ
 * - トークンはメモリ上の状態として保持
 * - 有効期限切れ時は再取得
 */
const TEMP_TOKEN_API = '/api/temp_token.json';

/**
 * 一時トークンのメモリ上状態
 */
let tempTokenState: { token: string; expires: number } | null = null;

/**
 * HTMLから初期トークンを取得する
 * レイアウトに埋め込まれたトークンを使用することでAPIコールを削減
 * @returns 変換後のトークン文字列またはnull
 */
export function getInitialTempToken(): string | null {
  const tokenElement = document.querySelector('div[data-temp-token]');
  if (tokenElement) {
    const rawToken = tokenElement.getAttribute('data-temp-token');
    if (rawToken) {
      return convertString(rawToken);
    }
  }
  return null;
}

/**
 * 有効な一時トークンを取得（state→初期トークン→APIの順で取得）
 * @returns Promise<string> トークン文字列
 */
export async function getValidTempToken(): Promise<string> {
  const now = Date.now();

  // メモリ上のstateで有効なトークンがあればそれを返す
  if (tempTokenState && now < tempTokenState.expires) {
    return tempTokenState.token; // 変換済みのトークン
  }

  // HTMLから埋め込まれた初期トークンを取得
  const initialToken = getInitialTempToken();
  if (initialToken) {
    tempTokenState = { token: initialToken, expires: now + 50000 }; // 約50秒間有効と仮定
    return initialToken;
  }

  // 有効なトークンがない場合はAPIから取得
  const resp = await fetch(TEMP_TOKEN_API, { credentials: 'include' });
  if (!resp.ok) throw new Error('一時トークン取得失敗');
  const data = await resp.json();
  const expires = new Date(data.expires_at).getTime();

  // APIから返されるtokenは生なので変換する
  const convertedToken = convertString(data.token);
  tempTokenState = { token: convertedToken, expires };
  return convertedToken;
}

/**
 * 一時トークンを強制的に再取得（state更新）
 */
export async function refreshTempToken(): Promise<string> {
  const resp = await fetch(TEMP_TOKEN_API, { credentials: 'include' });
  if (!resp.ok) throw new Error('一時トークン取得失敗');
  const data = await resp.json();
  const expires = new Date(data.expires_at).getTime();

  // APIから返されるtokenは生なので変換する
  const convertedToken = convertString(data.token);
  tempTokenState = { token: convertedToken, expires };
  return convertedToken;
}

/**
 * トークンを変換する関数（セキュリティ強化のため）
 *
 * この関数は生のトークンを受け取り、複数のステップでセキュリティを強化した変換トークンを返します。
 * Ruby側のconvert_string関数と対をなす処理で、
 * クライアント側で変換、サーバー側で逆変換という流れでトークン検証を行います。
 *
 * 変換ステップ：
 * 1. 現在時刻の「分」に基づく文字の入れ替え（タイムベース要素）
 * 2. 前半20文字の2文字ずつ交互に入れ替え（転置）
 * 3. 後半部分の文字を逆順にする（反転）
 *
 * 受信者（サーバー）は同じ変換ロジックを逆順で適用することで元の値を復元できます。
 *
 * @param str 変換する生のトークン文字列
 * @returns 変換後のトークン文字列
 */
function convertString(str: string) {
  // nullチェック（Rubyと同等に）
  if (!str) return '';

  // 現在の「分」を取得（0-59）
  const currentMinute = new Date().getMinutes();

  // 文字列を配列に変換
  const chars = str.split('');

  // 「分」をインデックスとして使用（ただし文字列の長さを超えないよう調整）
  const minuteIndex = Math.min(currentMinute, chars.length - 1);

  // 文字列の長さが2以上で、minuteIndexが有効な場合に文字を入れ替え
  if (chars.length > 1 && minuteIndex > 0) {
    // インデックス1と「分」のインデックスにある文字を入れ替え
    [chars[1], chars[minuteIndex]] = [chars[minuteIndex], chars[1]];
  }

  // 配列を文字列に戻す
  const processedStr = chars.join('');

  // 前半部分（最初の20文字）を取得
  const firstHalf = processedStr.substring(0, 20);

  // 2文字ずつ入れ替え（Rubyと同等の処理に修正）
  let swappedFirstHalf = '';
  for (let i = 0; i < firstHalf.length; i += 2) {
    if (i + 1 < firstHalf.length) {
      swappedFirstHalf += firstHalf[i + 1] + firstHalf[i];
    } else {
      swappedFirstHalf += firstHalf[i];
    }
  }

  // 20文字目以降を逆順に
  const reversedSecondHalf = processedStr.substring(20).split('').reverse().join('');

  // 変換した前半と逆順にした後半を連結
  const result = swappedFirstHalf + reversedSecondHalf;
  return result;
}

/**
 * APIリクエストヘルパークラス
 */
class APIHelper {
  /**
   * GET リクエストを送信する
   * @param url - リクエスト先URL（相対パス）
   * @param config - フェッチ設定（オプション）
   * @returns レスポンスのJSONデータ
   */
  async get(url: string, config = {}): Promise<any> {
    return this._getWithTokenRetry(url, config, 1);
  }

  /**
   * トークン自動リトライ付きGET
   */
  private async _getWithTokenRetry(url: string, config: any, retryCount: number): Promise<any> {
    const fullUrl = `${appConfig.API_URI}${url}`;
    const token = await getValidTempToken();
    const finalConfig = {
      ...fetchConfig,
      ...config,
      headers: {
        ...fetchConfig.headers,
        ...(config.headers || {}),
        'X-Temp-Token': token,
      },
    };

    try {
      const response = await fetch(fullUrl, finalConfig);

      // HTTP 401エラーの場合はトークン再取得してリトライ
      if (response.status === 401) {
        if (retryCount > 0) {
          await refreshTempToken();
          return this._getWithTokenRetry(url, config, retryCount - 1);
        }
        throw new Error('認証エラー（401）が続いています（リトライ上限）');
      }

      const responseText = await response.text();
      let json;
      if (url.includes('/space_bookings') && !url.includes('encrypt=false')) {
        // Base64デコードしてからJSONパース
        try {
        const decoded = atob(responseText);
        json = JSON.parse(decoded);
        } catch (decodeError) {
          // Base64として不正な形式の場合は、直接JSONとしてパースを試みる
          json = JSON.parse(responseText);
        }
      } else {
        json = JSON.parse(responseText);
      }
      // invalid/expired token判定
      if (json && typeof json === 'object' && (json.error === 'Invalid or expired token' || json.error === 'invalid token' || json.error === 'expired token')) {
        if (retryCount > 0) {
          await refreshTempToken();
          return this._getWithTokenRetry(url, config, retryCount - 1);
        }
        throw new Error('一時トークンが無効または期限切れです（リトライ上限）');
      }
      return json;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * POST リクエストを送信する
   * @param url - リクエスト先URL（相対パス）
   * @param data - 送信するデータ
   * @returns レスポンスのJSONデータ
   */
  async post(url: string, data: any = {}): Promise<any> {
    const fullUrl = `${appConfig.API_URI}${url}`;

    try {
      const response = await fetch(fullUrl, {
        ...fetchConfig,
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const json = await response.json();
        return json;
      } else {
        throw new Error(`HTTP Error: ${response.status}`);
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }
}

// シングルトンインスタンスをエクスポート
export default new APIHelper();